import type { Achievement, Competition, Equipment, Match, MatchEvent, Member, Team, Training } from "../../services/sportsService";
import { byNumber, byText, humanise, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * School sport's registers -- fixtures, competitions, teams and squads,
 * training, equipment and achievements -- and the rules the screens
 * explain. Choices mirror apps/sports/models.py.
 */

export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
export const clock = (value?: string | null) => (value ? value.slice(0, 5) : "");
export const labelFrom = (pairs: [string, string][], value?: string | null) => pairs.find(([key]) => key === value)?.[1] ?? humanise(value);

export const SPORT_TYPES: [string, string][] = [["team", "Team sport"], ["individual", "Individual sport"], ["recreational", "Recreational"], ["fitness", "Fitness"], ["academic", "Academic contest"], ["cultural", "Cultural"]];
export const POSITIONS: [string, string][] = [["player", "Player"], ["captain", "Captain"], ["vice_captain", "Vice captain"], ["substitute", "Substitute"], ["reserve", "Reserve"]];
export const COMPETITION_TYPES: [string, string][] = [["tournament", "Tournament"], ["league", "League"], ["friendly", "Friendly"], ["championship", "Championship"], ["exhibition", "Exhibition"]];
export const MATCH_TYPES: [string, string][] = [["friendly", "Friendly"], ["group_stage", "Group stage"], ["quarter_final", "Quarter-final"], ["semi_final", "Semi-final"], ["final", "Final"], ["knockout", "Knockout"]];
export const EVENT_TYPES: [string, string][] = [
  ["goal", "Goal"],
  ["assist", "Assist"],
  ["penalty", "Penalty scored"],
  ["own_goal", "Own goal"],
  ["yellow_card", "Yellow card"],
  ["red_card", "Red card"],
  ["substitution", "Substitution"],
  ["injury", "Injury"],
  ["other", "Other"],
];
export const EQUIPMENT_TYPES: [string, string][] = [["ball", "Balls"], ["uniform", "Uniforms"], ["protective", "Protective gear"], ["training", "Training kit"], ["field", "Field equipment"], ["other", "Other"]];
/** Hand-recorded equipment movements; mirrors SportsEquipmentViewSet.transact. */
export const EQUIPMENT_MOVES: { value: string; label: string; hint: string }[] = [
  { value: "receipt", label: "New stock in", hint: "Adds to the total and to what is available." },
  { value: "issue", label: "Issued out", hint: "Taken from what is available, e.g. to a team for a match." },
  { value: "return", label: "Returned", hint: "Back from being issued." },
  { value: "damage", label: "Damaged", hint: "Moved from available to damaged." },
  { value: "repair", label: "Repaired", hint: "Moved from damaged back to available." },
];
export const ACHIEVEMENT_TYPES: [string, string][] = [["championship", "Championship"], ["tournament_win", "Tournament win"], ["individual_award", "Individual award"], ["team_award", "Team award"], ["record", "Record"], ["recognition", "Recognition"]];
export const RATINGS: [string, string][] = [["5", "Excellent"], ["4", "Good"], ["3", "Average"], ["2", "Below average"], ["1", "Poor"]];
/** Events that count for the scorer's tally when the match is completed. */
export const SCORING_EVENTS = ["goal", "penalty"];

// --- Fixtures --------------------------------------------------------------------------------

export function matchStatus(match: Pick<Match, "status" | "match_date">, today: string) {
  switch (match.status) {
    case "in_progress":
      return { label: "Playing now", dot: "bg-sky-500" };
    case "completed":
      return { label: "Result in", dot: "bg-emerald-500" };
    case "cancelled":
      return { label: "Cancelled", dot: "bg-gray-400" };
    case "postponed":
      return { label: "Postponed", dot: "bg-amber-500" };
    default:
      if (match.match_date < today) return { label: "Result missing", dot: "bg-red-500" };
      if (match.match_date === today) return { label: "Today", dot: "bg-amber-500" };
      return { label: "Coming up", dot: "bg-indigo-500" };
  }
}

export const isOpenMatch = (match: Pick<Match, "status">) => ["scheduled", "postponed", "in_progress"].includes(match.status);

/** "Lions 2–1 Eagles", or "Lions v Eagles" before the result. */
export const scoreLine = (match: Pick<Match, "home_team_name" | "away_team_name" | "home_score" | "away_score" | "status">) =>
  match.status === "completed" && match.home_score != null && match.away_score != null
    ? `${match.home_team_name} ${match.home_score}–${match.away_score} ${match.away_team_name}`
    : `${match.home_team_name} v ${match.away_team_name}`;

export const matchConfig = (today: string): RegisterConfig<Match> => ({
  search: (row) => [row.home_team_name, row.away_team_name, row.competition_name, row.venue, row.referee].join(" "),
  views: [
    { key: "upcoming", label: "Coming up", match: (row) => (row.status === "scheduled" || row.status === "postponed") && row.match_date >= today },
    { key: "live", label: "Playing now", match: (row) => row.status === "in_progress" },
    { key: "missing", label: "Result missing", match: (row) => (row.status === "scheduled" || row.status === "postponed") && row.match_date < today },
    { key: "results", label: "Results", match: (row) => row.status === "completed" },
    { key: "cancelled", label: "Cancelled", match: (row) => row.status === "cancelled" },
    { key: "all", label: "All" },
  ],
  date: { label: "Match date", value: (row) => row.match_date },
  category: { label: "Competition", value: (row) => row.competition_name },
  sorts: [
    { key: "when", label: "Soonest first", compare: byText<Match>((row) => `${row.match_date}${row.match_time}`) },
    { key: "-when", label: "Latest first", compare: reversed(byText<Match>((row) => `${row.match_date}${row.match_time}`)) },
  ],
});

/** Goals per side from the recorded events, so the result form starts from what was logged. */
export function tallyGoals(match: Pick<Match, "home_team" | "away_team">, events: Pick<MatchEvent, "event_type" | "player">[], squad: Pick<Member, "id" | "team">[]) {
  const teamOf = new Map(squad.map((member) => [member.id, member.team]));
  let home = 0;
  let away = 0;
  for (const event of events) {
    const team = teamOf.get(event.player);
    if (SCORING_EVENTS.includes(event.event_type)) {
      if (team === match.home_team) home += 1;
      else if (team === match.away_team) away += 1;
    } else if (event.event_type === "own_goal") {
      // An own goal counts for the other side.
      if (team === match.home_team) away += 1;
      else if (team === match.away_team) home += 1;
    }
  }
  return { home, away };
}

// --- Competitions ----------------------------------------------------------------------------

export function competitionStatus(row: Pick<Competition, "status" | "start_date" | "end_date">, today: string) {
  switch (row.status) {
    case "in_progress":
      return { label: "Under way", dot: "bg-sky-500" };
    case "completed":
      return { label: "Finished", dot: "bg-emerald-500" };
    case "cancelled":
      return { label: "Cancelled", dot: "bg-gray-400" };
    default:
      return row.start_date <= today ? { label: "Due to start", dot: "bg-amber-500" } : { label: "Coming up", dot: "bg-indigo-500" };
  }
}

export const competitionConfig: RegisterConfig<Competition> = {
  search: (row) => [row.name, row.sport_name, row.venue, row.organizer].join(" "),
  views: [
    { key: "current", label: "Current", match: (row) => row.status === "scheduled" || row.status === "in_progress" },
    { key: "finished", label: "Finished", match: (row) => row.status === "completed" },
    { key: "cancelled", label: "Cancelled", match: (row) => row.status === "cancelled" },
    { key: "all", label: "All" },
  ],
  date: { label: "Starts", value: (row) => row.start_date },
  category: { label: "Sport", value: (row) => row.sport_name },
  sorts: [
    { key: "start", label: "Starts soonest", compare: byText<Competition>((row) => row.start_date) },
    { key: "-start", label: "Latest first", compare: reversed(byText<Competition>((row) => row.start_date)) },
  ],
};

/** Played, won, drawn, lost and goals for each team in a competition's completed matches. */
export function standings(matches: Match[]) {
  const table = new Map<number, { team: number; name: string; played: number; won: number; drawn: number; lost: number; scored: number; conceded: number; points: number }>();
  const row = (team: number, name: string) => {
    if (!table.has(team)) table.set(team, { team, name, played: 0, won: 0, drawn: 0, lost: 0, scored: 0, conceded: 0, points: 0 });
    return table.get(team)!;
  };
  for (const match of matches) {
    if (match.status !== "completed" || match.home_score == null || match.away_score == null) continue;
    const home = row(match.home_team, match.home_team_name);
    const away = row(match.away_team, match.away_team_name);
    home.played += 1;
    away.played += 1;
    home.scored += match.home_score;
    home.conceded += match.away_score;
    away.scored += match.away_score;
    away.conceded += match.home_score;
    if (match.home_score > match.away_score) {
      home.won += 1;
      away.lost += 1;
      home.points += 3;
    } else if (match.home_score < match.away_score) {
      away.won += 1;
      home.lost += 1;
      away.points += 3;
    } else {
      home.drawn += 1;
      away.drawn += 1;
      home.points += 1;
      away.points += 1;
    }
  }
  return [...table.values()].sort((a, b) => b.points - a.points || b.scored - b.conceded - (a.scored - a.conceded) || b.scored - a.scored || a.name.localeCompare(b.name));
}

// --- Teams -----------------------------------------------------------------------------------

export const teamConfig: RegisterConfig<Team> = {
  search: (row) => [row.name, row.sport_name, row.coach_name, row.captain_name, row.season].join(" "),
  views: [
    { key: "active", label: "Active", match: (row) => row.is_active },
    { key: "inactive", label: "Stood down", match: (row) => !row.is_active },
    { key: "all", label: "All" },
  ],
  category: { label: "Sport", value: (row) => row.sport_name },
  sorts: [
    { key: "name", label: "Name (A–Z)", compare: byText<Team>((row) => row.name) },
    { key: "sport", label: "Sport", compare: byText<Team>((row) => `${row.sport_name} ${row.name}`) },
    { key: "-year", label: "Newest season", compare: reversed(byNumber<Team>((row) => row.year)) },
  ],
};

/** The squad in team-sheet order: captain, vice-captain, players, then the bench. */
export function squadOrder(members: Member[]) {
  const rank: Record<string, number> = { captain: 0, vice_captain: 1, player: 2, substitute: 3, reserve: 4 };
  return [...members].sort((a, b) => Number(b.is_active) - Number(a.is_active) || (rank[a.position] ?? 9) - (rank[b.position] ?? 9) || (a.jersey_number ?? 999) - (b.jersey_number ?? 999) || a.student_name.localeCompare(b.student_name));
}

// --- Training --------------------------------------------------------------------------------

export function trainingStatus(row: Pick<Training, "status" | "session_date">, today: string) {
  switch (row.status) {
    case "in_progress":
      return { label: "On now", dot: "bg-sky-500" };
    case "completed":
      return { label: "Done", dot: "bg-emerald-500" };
    case "cancelled":
      return { label: "Cancelled", dot: "bg-gray-400" };
    default:
      if (row.session_date < today) return { label: "Not held", dot: "bg-red-500" };
      if (row.session_date === today) return { label: "Today", dot: "bg-amber-500" };
      return { label: "Coming up", dot: "bg-indigo-500" };
  }
}

export const trainingConfig = (today: string): RegisterConfig<Training> => ({
  search: (row) => [row.team_name, row.venue, row.focus_area, row.conducted_by_name].join(" "),
  views: [
    { key: "upcoming", label: "Coming up", match: (row) => (row.status === "scheduled" || row.status === "in_progress") && row.session_date >= today },
    { key: "done", label: "Done", match: (row) => row.status === "completed" },
    { key: "missed", label: "Not held", match: (row) => row.status === "scheduled" && row.session_date < today },
    { key: "all", label: "All" },
  ],
  date: { label: "Date", value: (row) => row.session_date },
  category: { label: "Team", value: (row) => row.team_name },
  sorts: [
    { key: "when", label: "Soonest first", compare: byText<Training>((row) => `${row.session_date}${row.start_time}`) },
    { key: "-when", label: "Latest first", compare: reversed(byText<Training>((row) => `${row.session_date}${row.start_time}`)) },
  ],
});

// --- Equipment -------------------------------------------------------------------------------

export function equipmentStatus(row: Pick<Equipment, "is_active" | "total_quantity" | "available_quantity" | "damaged_quantity">) {
  if (!row.is_active) return { label: "Not used", dot: "bg-gray-400" };
  if (!row.total_quantity) return { label: "None in stock", dot: "bg-gray-400" };
  if (row.available_quantity === 0) return { label: "All out or damaged", dot: "bg-red-500" };
  if (row.damaged_quantity > 0) return { label: `${row.damaged_quantity} damaged`, dot: "bg-amber-500" };
  return { label: "Ready", dot: "bg-emerald-500" };
}

/** Where a movement would leave the counts; mirrors transact. Null when it is not possible. */
export function afterMove(row: Pick<Equipment, "total_quantity" | "available_quantity" | "damaged_quantity">, move: string, quantity: number) {
  const changes: Record<string, [number, number, number]> = {
    receipt: [quantity, quantity, 0],
    issue: [0, -quantity, 0],
    return: [0, quantity, 0],
    damage: [0, -quantity, quantity],
    repair: [0, quantity, -quantity],
  };
  const [total, available, damaged] = changes[move] ?? [0, 0, 0];
  const next = { total: row.total_quantity + total, available: row.available_quantity + available, damaged: row.damaged_quantity + damaged };
  if (quantity < 1 || Math.min(next.total, next.available, next.damaged) < 0 || next.available + next.damaged > next.total) return null;
  return next;
}

export const equipmentConfig: RegisterConfig<Equipment> = {
  search: (row) => [row.name, row.sport_name, row.storage_location, row.description].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "damaged", label: "Has damaged items", match: (row) => row.damaged_quantity > 0 },
    { key: "out", label: "Nothing available", match: (row) => row.total_quantity > 0 && row.available_quantity === 0 },
  ],
  category: { label: "Kind", value: (row) => labelFrom(EQUIPMENT_TYPES, row.equipment_type) },
  sorts: [
    { key: "name", label: "Name (A–Z)", compare: byText<Equipment>((row) => row.name) },
    { key: "available", label: "Least available", compare: byNumber<Equipment>((row) => row.available_quantity) },
  ],
};

// --- Achievements ----------------------------------------------------------------------------

export const achievementConfig: RegisterConfig<Achievement> = {
  search: (row) => [row.student_name, row.title, row.team_name, row.competition_name, row.award_level].join(" "),
  views: [{ key: "all", label: "All" }],
  date: { label: "Date", value: (row) => row.achieved_date },
  category: { label: "Kind", value: (row) => labelFrom(ACHIEVEMENT_TYPES, row.achievement_type) },
  sorts: [
    { key: "-when", label: "Newest first", compare: reversed(byText<Achievement>((row) => row.achieved_date)) },
    { key: "learner", label: "Learner (A–Z)", compare: byText<Achievement>((row) => row.student_name) },
  ],
};
